import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type DiffData = {
  mode: 'history' | 'cleanup';
  entityKind: 'document' | 'canvas';
  entityId: string;
  /** Required for history mode; omit for cleanup mode (no specific snapshot to reference). */
  snapshotId?: string;
  timestamp: string;
  /** Optional snapshot name set by the user */
  name?: string | null;
  /** cleanup mode only — encoded Y.Doc state (Uint8Array from Y.encodeStateAsUpdate) */
  suggestedState?: Uint8Array;
};

export type AssistantMode = 'hidden' | 'collapsed' | 'expanded' | 'fullTab' | 'sidebar';

/** What a publisher knows about the surface it is making active. `surface` is
 * the manifest's open kind string ('audio' | 'text' | 'canvas' | future), kept
 * as a string for the same reason SurfaceEntry does. */
export interface ActiveSurfaceMeta {
  title: string;
  surface: string;
  /** The recording this surface holds, when it holds one. Travels with the
   * surface because `useRecordingForSurface` needs it to resolve a recording
   * exactly: without it a consumer falls back to the document's most recent
   * recording, which in a document holding several meetings is a different
   * meeting. */
  recordingId: string | null;
}

export type ActiveSurface = ActiveSurfaceMeta & { id: string };

// ── Store ────────────────────────────────────────────────────────────────────

interface UIState {
  editorRevision: number;
  bumpEditorRevision: () => void;
  editorStats: { wordCount: number; characterCount: number };
  setStats: (words: number, chars: number) => void;
  diffView: DiffData | null;
  setDiffView: (view: DiffData | null) => void;
  isSearchOpen: boolean;
  setSearchOpen: (open: boolean) => void;
  isSettingsOpen: boolean;
  /** Tab the settings modal should open to (e.g. deep-link to 'subscription'). */
  settingsInitialTab: string | null;
  setSettingsOpen: (open: boolean, initialTab?: string) => void;
  isLeftSidebarOpen: boolean;
  toggleLeftSidebar: () => void;
  isFindBarVisible: boolean;
  toggleFindBar: () => void;
  setFindBarVisible: (open: boolean) => void;
  isStatusBarVisible: boolean;
  isCommentsPanelOpen: boolean;
  setCommentsPanelOpen: (open: boolean) => void;
  isVersionHistoryOpen: boolean;
  setVersionHistoryOpen: (open: boolean) => void;
  isAssistantHistoryOpen: boolean;
  setAssistantHistoryOpen: (open: boolean) => void;
  isAttendeesPanelOpen: boolean;
  /** The entity the open attendees panel targets. Null means "defer to the
   * active entity" (e.g. a global opener outside split view). */
  attendeesPanelEntityId: string | null;
  /** The surface (within attendeesPanelEntityId) the panel is scoped to.
   * Null means the document-level roster. */
  attendeesPanelSurfaceId: string | null;
  setAttendeesPanelOpen: (open: boolean, entityId?: string | null, surfaceId?: string | null) => void;
  /** Which surface is active per document, keyed by entityId so two documents
   * open side by side (split view) never share one selection — the exact bug
   * class the M7 AttendeesChip fix exists to prevent. Plain object rather
   * than a Map: zustand's persist middleware and devtools serialize objects
   * cleanly; a Map would need custom (de)serialization for no benefit here. */
  activeSurfaceIdByEntity: Record<string, string | null>;
  /** The active surface's name and kind, published alongside its id. The title
   * bar renders "document › surface" but sits above SurfaceMount and cannot
   * reach the manifest — components/ may not import features/. SurfaceMount
   * already holds the entry, so it publishes what the header needs instead of
   * the header opening a second Yjs subscription to re-derive it. */
  activeSurfaceByEntity: Record<string, ActiveSurface | null>;
  /** A pending request to change the active surface, for SurfaceMount to
   * honour and then clear. SurfaceMount owns the selection and only mirrors it
   * into this store, so a consumer that wants to change it asks rather than
   * writes — clearing the mirror alone would leave a surface body open under a
   * document heading. Wrapped in an object because `null` is a real
   * destination (the document body) and must not also mean "no request". */
  requestedSurfaceByEntity: Record<string, { surfaceId: string | null } | null>;
  getActiveSurfaceId: (entityId: string) => string | null;
  getActiveSurface: (entityId: string) => ActiveSurface | null;
  getRequestedSurface: (entityId: string) => { surfaceId: string | null } | null;
  setActiveSurfaceId: (
    entityId: string,
    surfaceId: string | null,
    meta?: ActiveSurfaceMeta,
  ) => void;
  requestSurface: (entityId: string, surfaceId: string | null) => void;
  clearSurfaceRequest: (entityId: string) => void;
  selectionRequestByEntity: Record<string, SelectionRequest | null>;
  requestFromSelection: (entityId: string, request: SelectionRequest) => void;
  /** Reads and clears the entity's request inside one `set`, returning what it read. */
  takeSelectionRequest: (entityId: string) => SelectionRequest | null;
  assistantMode: AssistantMode;
  setAssistantMode: (mode: AssistantMode) => void;
  isAssistantSidebarOpen: boolean;
  toggleAssistantSidebar: () => void;
  isZenMode: boolean;
  setZenMode: (isActive: boolean) => void;
}

export interface SelectionRequest {
  fragmentName: string;
  markdown: string;
  templateId: string | null;
}

export const useUIStore = create<UIState>()(persist((set, get) => ({
  editorRevision: 0,
  bumpEditorRevision: () => set((s) => ({ editorRevision: s.editorRevision + 1 })),
  editorStats: { wordCount: 0, characterCount: 0 },
  setStats: (words, chars) => set({ editorStats: { wordCount: words, characterCount: chars } }),
  diffView: null,
  setDiffView: (view) => set({ diffView: view }),
  isSearchOpen: false,
  setSearchOpen: (open) => set({ isSearchOpen: open }),
  isSettingsOpen: false,
  settingsInitialTab: null,
  setSettingsOpen: (open, initialTab) =>
    set({ isSettingsOpen: open, settingsInitialTab: open ? initialTab ?? null : null }),
  isLeftSidebarOpen: true,
  toggleLeftSidebar: () => set((s) => ({ isLeftSidebarOpen: !s.isLeftSidebarOpen })),
  isFindBarVisible: false,
  toggleFindBar: () => set((s) => ({ isFindBarVisible: !s.isFindBarVisible })),
  setFindBarVisible: (open) => set({ isFindBarVisible: open }),
  isStatusBarVisible: false,
  isCommentsPanelOpen: false,
  setCommentsPanelOpen: (open) => set({ isCommentsPanelOpen: open }),
  isVersionHistoryOpen: false,
  setVersionHistoryOpen: (open) => set({ isVersionHistoryOpen: open }),
  isAssistantHistoryOpen: false,
  setAssistantHistoryOpen: (open) => set({ isAssistantHistoryOpen: open }),
  isAttendeesPanelOpen: false,
  attendeesPanelEntityId: null,
  attendeesPanelSurfaceId: null,
  setAttendeesPanelOpen: (open, entityId = null, surfaceId = null) =>
    set({
      isAttendeesPanelOpen: open,
      attendeesPanelEntityId: open ? entityId : null,
      attendeesPanelSurfaceId: open ? surfaceId : null,
    }),
  activeSurfaceIdByEntity: {},
  activeSurfaceByEntity: {},
  requestedSurfaceByEntity: {},
  getActiveSurfaceId: (entityId) => get().activeSurfaceIdByEntity[entityId] ?? null,
  getActiveSurface: (entityId) => get().activeSurfaceByEntity[entityId] ?? null,
  getRequestedSurface: (entityId) => get().requestedSurfaceByEntity[entityId] ?? null,
  requestSurface: (entityId, surfaceId) =>
    set((s) => ({
      requestedSurfaceByEntity: { ...s.requestedSurfaceByEntity, [entityId]: { surfaceId } },
    })),
  clearSurfaceRequest: (entityId) =>
    set((s) => ({
      requestedSurfaceByEntity: { ...s.requestedSurfaceByEntity, [entityId]: null },
    })),
  selectionRequestByEntity: {},
  requestFromSelection: (entityId, request) =>
    set((s) => ({
      selectionRequestByEntity: { ...s.selectionRequestByEntity, [entityId]: request },
    })),
  takeSelectionRequest: (entityId) => {
    let taken: SelectionRequest | null = null;
    set((s) => {
      taken = s.selectionRequestByEntity[entityId] ?? null;
      return { selectionRequestByEntity: { ...s.selectionRequestByEntity, [entityId]: null } };
    });
    return taken;
  },
  setActiveSurfaceId: (entityId, surfaceId, meta) =>
    set((s) => ({
      activeSurfaceIdByEntity: { ...s.activeSurfaceIdByEntity, [entityId]: surfaceId },
      activeSurfaceByEntity: {
        ...s.activeSurfaceByEntity,
        [entityId]: surfaceId && meta ? { id: surfaceId, ...meta } : null,
      },
    })),
  assistantMode: 'hidden',
  setAssistantMode: (mode) => set({ assistantMode: mode }),
  isAssistantSidebarOpen: false,
  toggleAssistantSidebar: () => set((s) => ({ isAssistantSidebarOpen: !s.isAssistantSidebarOpen })),
  isZenMode: false,
  setZenMode: (isActive) => set({ isZenMode: isActive }),
}), {
  name: 'nula-ui',
  partialize: (s) => ({ isLeftSidebarOpen: s.isLeftSidebarOpen, isZenMode: s.isZenMode }),
}));
